import { memo, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
// Local files
import {
  Root,
  Header,
  Title,
  StatusBanner,
  StatusIndicator,
  StatusText,
  UptimeText,
  Section,
  SectionTitle,
  ComponentCard,
  ComponentInfo,
  ComponentName,
  ComponentStatus,
  IncidentCard,
  IncidentHeader,
  IncidentName,
  IncidentStatus,
  IncidentMeta,
  LastUpdated,
  LoadingContainer,
  EmptyState
} from './Status.styled';
import useStatus from 'hooks/useStatus';
import useCustomSelector from 'hooks/useCustomSelector';

const STATUS_LABELS = {
  operational: 'All Systems Operational',
  degraded: 'Degraded Performance',
  partial_outage: 'Partial System Outage',
  outage: 'Major System Outage',
  unknown: 'Status Unknown'
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return formatDate(dateString);
};

const Status = () => {
  const { getStatus, refreshStatus } = useStatus();
  const { status, loading, refreshing, lastFetched } = useCustomSelector(state => state.status);

  useEffect(() => {
    getStatus();
  }, [getStatus]);

  const overallStatus = useMemo(() => status?.status || 'unknown', [status]);
  const uptimePercentage = useMemo(() => status?.uptime_percentage, [status]);
  const components = useMemo(() => status?.components || [], [status]);
  const incidents = useMemo(() => status?.recent_incidents || [], [status]);

  const handleRefresh = () => {
    refreshStatus();
  };

  if (loading && !status) {
    return (
      <Root>
        <LoadingContainer>
          <CircularProgress size={40} />
          <Typography color="textSecondary">Loading system status...</Typography>
        </LoadingContainer>
      </Root>
    );
  }

  return (
    <Root>
      <Header>
        <Title>System Status</Title>
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Header>

      <StatusBanner status={overallStatus}>
        <Box display="flex" alignItems="center" gap={2}>
          <StatusIndicator status={overallStatus} />
          <StatusText status={overallStatus}>
            {STATUS_LABELS[overallStatus] || 'Status Unknown'}
          </StatusText>
        </Box>
        {uptimePercentage && (
          <UptimeText>
            {uptimePercentage}% uptime over the last 30 days
          </UptimeText>
        )}
      </StatusBanner>

      <Section>
        <SectionTitle>External Services</SectionTitle>
        {_isEmpty(components) ? (
          <EmptyState>
            <Typography>No services are being monitored</Typography>
          </EmptyState>
        ) : (
          _map(components, (component) => (
            <ComponentCard key={component.id} elevation={1}>
              <ComponentInfo>
                <StatusIndicator status={component.status} />
                <ComponentName>{component.name}</ComponentName>
              </ComponentInfo>
              <Box display="flex" alignItems="center" gap={2}>
                {component.response_time && (
                  <Typography variant="body2" color="textSecondary">
                    {component.response_time}ms
                  </Typography>
                )}
                <ComponentStatus status={component.status}>
                  {component.status}
                </ComponentStatus>
              </Box>
            </ComponentCard>
          ))
        )}
      </Section>

      <Section>
        <SectionTitle>Recent Incidents</SectionTitle>
        {_isEmpty(incidents) ? (
          <EmptyState>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography>No recent incidents</Typography>
            <Typography variant="body2" color="textSecondary">
              All systems have been running smoothly
            </Typography>
          </EmptyState>
        ) : (
          _map(incidents, (incident) => (
            <IncidentCard key={incident.id} elevation={1} resolved={incident.status === 'resolved'}>
              <IncidentHeader>
                <IncidentName>{incident.name}</IncidentName>
                <IncidentStatus resolved={incident.status === 'resolved'}>
                  {incident.status}
                </IncidentStatus>
              </IncidentHeader>
              <IncidentMeta>
                {formatDate(incident.started_at)}
                {incident.duration_minutes && ` • Resolved in ${incident.duration_minutes} minutes`}
              </IncidentMeta>
            </IncidentCard>
          ))
        )}
      </Section>

      {lastFetched && (
        <LastUpdated>
          Last checked: {formatRelativeTime(lastFetched)}
        </LastUpdated>
      )}
    </Root>
  );
};

export default memo(Status);

